import React, { useState } from 'react';
import { Flex, Text, Box, Image } from 'rebass';
import Wrapper from './Wrapper';
import CollapsibleSection from './CollapsibleSection';
import certificate_preview from './assets/certificate_preview.png';
import { FaRegCircleQuestion } from "react-icons/fa6";
import ResponsiveReactPlayer from 'story-media/medium-types/ResponsiveReactPlayer';

const FAQS = [
    {
        question: 'Can I start during pregnancy?', answer: <>
            Absolutely! While most of your memories will come after your baby is born, pregnancy is a special chapter in your child’s story — one they’ll love hearing about someday.
            <br />
            <br />
            This is the perfect time to capture moments like:
            {['The story behind choosing their name and its meaning',
                'Ultrasound pictures and your thoughts when you first saw them',
                'How you felt hearing their heartbeat for the first time',
                'Funny, emotional, or unexpected moments along the way'
            ].map(title => <Flex alignItems="baseline" py="1%">
                <Text minWidth={8} height={8} mr={1} textAlign="center" sx={{ borderRadius: '50%', border: '2px solid #4F4F4F', backgroundColor: '#4F4F4F' }} />
                <Text
                    color="#64748B"
                    fontFamily="Poppins"
                    fontSize={"16px"}
                    lineHeight="22px"
                >{title}</Text>
            </Flex>)}
            <br />
            <br />
            By starting today, moms can create a fuller, richer story — one that begins before your baby even arrives.
        </>
    },
    { question: 'Can I start after the baby is born and back-date Memories?', answer: 'Yes. You can easily add earlier Memories and specify the date. They’ll be auto-organized chronologically in your chapters.' },
    {
        question: 'Can I do this even if my baby is older than 1?', answer: <>
            Yes. You can use Baby Yearbooks to create a keepsake for a child of any age. While most people create old style memory books just for their baby’s first year, they usually create photo books for subsequent years.
            <br />
            <br />
            With our ease of use and many advantages over both types, moms are already telling us that they plan to do this for future years as well.</>
    },
    { question: 'How many pages in the book?', answer: 'Our base price includes 75 pages. However, you have the option of making it bigger — the book can even have as many as 500 pages.  The price will be based on costs at that time — currently $1.00 per extra page.' },
    { question: 'How long can a memory be? What are the limitations?', answer: 'Memories can be any length and can be made up of any combination of text, pictures and video, in any order. ' },
    {
        question: 'Can Grandparents add memories?', answer:
            <>
                Yes. We are very excited about this feature!
                <br />
                <br />
                Mom can invite grandparents (and other loved ones) to share Memories as well.
                <br />
                <br />
                Imagine, decades from now, being able to view a video — not just any video with grandparents in it — but one in which they’re telling a story to the baby … when they were still a baby … in their special book.
                <br />
                <br />
                Gives us goosebumps!
            </>

    },
    {
        question: 'How does our price compare with other photo book services?', answer:
            <>
                When you compare apples to apples, our price is highly competitive—often even lower than their promotional pricing. And unlike traditional photo books, ours includes <b>videos and rich storytelling.</b>
                <br />
                <br />
                Consider these specs:
                {[
                    <><b>Hardcover book</b> with <b>photo-quality matte pages</b></>,
                    <b>8.5" x 11" size</b>,
                    <b>75 pages</b>,
                ].map(title => <Flex alignItems="baseline" py="1%">
                    <Text minWidth={8} height={8} mr={1} textAlign="center" sx={{ borderRadius: '50%', border: '2px solid #4F4F4F', backgroundColor: '#4F4F4F' }} />
                    <Text
                        color="#64748B"
                        fontFamily="Poppins"
                        fontSize={"16px"}
                        lineHeight="22px"
                    >{title}</Text>
                </Flex>)}
                <br />
                <br />
                Other services may offer <b>lower-cost options with smaller sizes, soft covers, fewer pages, or lower-quality paper</b>, but those compromises come at the expense of quality and storytelling.
                <br />
                <br />
                A few dollars saved today won’t compare to the <b>lasting value</b> of a book you’ll cherish forever. At Baby Yearbooks, we make it easy to create a <b>beautiful keepsake</b>—at a great price!</>

    },
    {
        question: 'How will our private information be stored?', answer: <>Your privacy is our priority. All data is securely stored using encrypted servers, and only mom can access the account. We never sell or share your information. For further details, see our <Text as="a" href='https://www.babyyearbooks.com/privacy' target='_blank' color="#9B59B6">privacy policy</Text>.</>

    },
]

function Option({ index, title }) {
    return <Flex mb={2}>
        <Text fontSize={14} minWidth={24} height={24}
            mr={1}
            textAlign="center" bg="#F1FCE9"
            sx={{ borderRadius: '50%', border: '2px solid #C2F1A5' }} >{index}</Text>
        <Text
            sx={{
                color: "#64748B",
                fontFamily: 'Poppins',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '150%',
                letterSpacing: '-0.096px',
            }}
        >
            {title}
        </Text>
    </Flex>
}

const FAQSection = ({ hideSkip, onStepUp, onStepDown, isForMyBaby }) => {
    const [openQuestion, setOpenQuestion] = useState(null);

    function sendEmail() {
        const title = `Question about Baby Yearbooks`;

        const mailto = `mailto:hello@babyyearbooks.com?subject=${encodeURIComponent(title)}`;

        window.open(mailto, '_blank')
    }

    return (
        <Wrapper
            hideSkip={hideSkip}
            onSubmit={onStepUp}
            showFooter
            onBack={onStepDown}
            percentComplete={0}
            bg="aliceBlue"
        >
            {isForMyBaby ? <Box m="auto" mt="4.7%" mb="2%" width={['92%', '92%', '33%']} minWidth={['max-width', 480]}>
                <Text my="30px" color="codGrey"
                    fontFamily="Bitter"
                    fontSize={30}
                    lineHeight="120%"
                    letterSpacing="-0.18px"
                    textAlign="left">A Book You’ll Cherish Forever</Text>

                <ResponsiveReactPlayer
                    playerWrapper={{ width: '100%' }}
                    height='330px'
                    playing={true}
                    muted={true}
                    controls={false}
                    loop
                    preload="auto"
                    playsinline={true}
                    url="https://player.vimeo.com/video/1054560556"
                    config={{
                        file: {
                            attributes: {
                                preload: true
                            }
                        },
                        vimeo: { preload: true }
                    }}
                />
            </Box>
                : <Flex flexDirection="column" alignItems="flex-start" justifyContent="center" m="auto" mt="4.7%" mb="2%" width={['92%', '92%', '33%']} minWidth={['max-width', 480]}>
                    <Text mb="3.9%" color="codGrey"
                        fontFamily="Bitter"
                        fontSize={30}
                        lineHeight="120%"
                        letterSpacing="-0.18px"
                        textAlign="left">
                        Here’s what’s next!
                    </Text>
                    <Option index={1} title="Complete your purchase" />
                    <Option index={2} title="Personalize your gift certificate" />
                    <Option index={3} title="Receive your PDF via email" />
                    <Text
                        sx={{
                            color: "#64748B",
                            fontFamily: 'Poppins',
                            fontSize: '16px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '150%',
                            letterSpacing: '-0.096px',
                        }}
                    >
                        Print it out and gift it in person or share it via email or messaging.
                    </Text>
                </Flex>}
            {!isForMyBaby && <Flex justifyContent="center" alignItems="center" mb="2.7%">
                <Image src={certificate_preview} width={['100%', '100%', '33%']} sx={{ borderRadius: ['unset', 'unset', '10px'] }} />
            </Flex>}

            {/* <Box bg="#F5EEF8" width="100vw" py="40px">
                <Flex flexDirection="column" alignItems="flex-start" justifyContent="center" m="auto" width={['92%', '92%', '33%']} minWidth={['max-width', 480]}>
                    <Text mb="3.9%" color="codGrey"
                        fontFamily="Bitter"
                        fontSize={30}
                        lineHeight="120%"
                        letterSpacing="-0.18px"
                        textAlign="left">FAQ's</Text>
                    {FAQS.map((quest, index) => <CollapsibleSection index={index} isOpen={openQuestion} setIsOpen={setOpenQuestion} label={quest?.question} >
                        <Text sx={{
                            color: "#4F4F4F",
                            fontFamily: "Poppins",
                            fontSize: "16px",
                            fontStyle: "normal",
                            fontWeight: 400,
                            lineHeight: "150%",
                        }}>
                            {quest?.answer}
                        </Text>
                    </CollapsibleSection>)}
                </Flex>
            </Box>
            <Flex justifyContent="center" alignItems="center" my="20px">
                <FaRegCircleQuestion color="#0E0E0E" size={20} />
                <Text
                    ml={1}
                    color="codGrey"
                    fontFamily="Poppins"
                    fontWeight={400}
                    letterSpacing="-0.096px"
                    fontSize={16}
                    lineHeight="120%"
                >
                    Have any more questions? <Text
                        as="span"
                        fontWeight={600}
                        color="#9B59B6"
                        onClick={sendEmail}
                        sx={{
                            textDecorationLine: 'underline',
                            cursor: 'pointer'
                        }}>Contact Us.</Text>
                </Text>
            </Flex> */}
        </Wrapper>
    )
};

export default FAQSection;
